import type {
    FilaInventario,
    OpcionCatalogo,
    QrFallido,
    QrProducto,
    ResultadoQrMasivo,
} from "../types/catalogoInventario.types";
import { urlImagenQr } from "./qrProducto.utils";

const esObjeto = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;
const aTexto = (v: unknown): string => (v === undefined || v === null ? "" : String(v).trim());
const aNumero = (v: unknown): number => Number(v) || 0;
const aLista = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

const crearQr = (idInventario: number, clave: unknown, archivo: unknown): QrProducto | null => {
    const texto = aTexto(clave);
    const imagen = aTexto(archivo);
    return texto && imagen ? { idInventario, clave: texto, urlImagen: urlImagenQr(imagen) } : null;
};

/** Convierte la respuesta de getInventario (snake_case, joins) a FilaInventario. */
export const normalizarInventario = (inventario: unknown): FilaInventario[] =>
    aLista(inventario).filter(esObjeto).map((i) => {
        const id = aNumero(i.id);
        return {
            id,
            clave: aTexto(i.clave),
            nombre: aTexto(i.nombre),
            precio: aNumero(i.precio),
            cantidad: aNumero(i.cantidad),
            maximos: aNumero(i.maximos),
            minimos: aNumero(i.minimos),
            puntoReorden: aNumero(i.punto_reorden),
            activo: aNumero(i.estatus) === 1,
            idUnidadMedida: aNumero(i.id_unidad_medida),
            idCategoria: aNumero(i.id_categoria),
            idUbicacion: aNumero(i.id_ubicacion),
            idTipoProducto: aNumero(i.id_tipo_producto),
            idAlmacen: aNumero(i.id_almacen),
            unidadMedida: aTexto(i.unidad_medida),
            categoria: aTexto(i.categoria),
            ubicacion: aTexto(i.ubicacion),
            almacen: aTexto(i.almacen),
            tipoProducto: aTexto(i.tipo_producto),
            fechaCreacion: aTexto(i.fecha_creacion),
            qr: crearQr(id, i.qr_clave, i.qr_url_imagen),
        };
    });

export const normalizarQr = (registro: unknown): QrProducto | null =>
    esObjeto(registro)
        ? crearQr(aNumero(registro.id_inventario), registro.clave, registro.url_imagen)
        : null;

const normalizarListaQr = (lista: unknown): QrProducto[] =>
    aLista(lista).map(normalizarQr).filter((q): q is QrProducto => q !== null);

export const normalizarResultadoQrMasivo = (respuesta: unknown): ResultadoQrMasivo => {
    const r = esObjeto(respuesta) ? respuesta : {};
    return {
        generados: normalizarListaQr(r.generados),
        existentes: normalizarListaQr(r.existentes),
        fallidos: aLista(r.fallidos)
            .filter(esObjeto)
            .map((f): QrFallido => ({ idInventario: aNumero(f.id_inventario), mensaje: aTexto(f.mensaje) })),
    };
};

export const normalizarCatalogo = (
    catalogo: unknown,
    campoId = "id",
    campoNombre = "nombre"
): OpcionCatalogo[] =>
    aLista(catalogo)
        .filter(esObjeto)
        .map((c) => ({ id: aNumero(c[campoId]), nombre: aTexto(c[campoNombre]) }))
        .filter((c) => c.id > 0);
